// =====================================================================
// crmCustomer — Pilots: Gatelog tab.
//
// One row per (pilot, gate) review. Auto-populates the gate date from
// the pilot's startDate plus the gate's dayOffset (see PILOT_GATES in
// pilots-shared.jsx) when adding a new entry; the user can still
// override. Filters by pilot so the cross-tab link from List
// (filterPilotId) opens a focused view.
// =====================================================================

// A gate entry is overdue when it's still pending (or has no decision)
// and its target date has passed. Drives the red row styling on the
// table.
function isGateOverdue(entry) {
  const pending = !entry.decision || entry.decision === "pending";
  if (!pending) return false;
  if (!entry.gateDate) return false;
  return entry.gateDate < new Date().toISOString().slice(0, 10);
}

// Two-position segmented toggle used for the gate decision flow:
//   Toggle 1: Pending ↔ Done
//   Toggle 2: Go ↔ No-go  (disabled while Toggle 1 is Pending)
// The pair lets the user "mark done" in two clicks while forcing them to
// pick a concrete verdict (go or no-go). The single string `decision` on
// the row (pending|go|no-go) is the source of truth; the toggles are
// just a presentation of it.
function GateDecisionToggles({ decision, onChange }) {
  const completed = decision === "go" || decision === "no-go";
  const goNoGo    = decision === "no-go" ? "no-go" : "go";
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <SegToggle
        options={[{ value: "pending", label: "Pending" }, { value: "done", label: "Done" }]}
        value={completed ? "done" : "pending"}
        onChange={(v) => {
          // Pending → reset to pending. Done → default to go (user can
          // flip to no-go on the second toggle).
          if (v === "pending") onChange("pending");
          else                 onChange(goNoGo);
        }} />
      <SegToggle
        options={[{ value: "go", label: "Go" }, { value: "no-go", label: "No-go" }]}
        value={goNoGo}
        disabled={!completed}
        onChange={(v) => onChange(v)} />
    </div>
  );
}

// Small two-button segmented control. Kept here because it's only used
// by the toggle pair above; promote to ui.jsx if a second caller appears.
function SegToggle({ options, value, onChange, disabled }) {
  return (
    <div style={{
      display: "inline-flex",
      border: "1px solid var(--divider, #e7e2d5)",
      borderRadius: 4,
      overflow: "hidden",
      opacity: disabled ? 0.5 : 1,
    }}>
      {options.map((opt, i) => {
        const active = value === opt.value;
        return (
          <button key={opt.value} type="button" disabled={disabled}
            onClick={() => !disabled && onChange(opt.value)}
            style={{
              padding: "5px 14px",
              fontSize: 12, fontWeight: 500,
              border: "none",
              borderLeft: i === 0 ? "none" : "1px solid var(--divider, #e7e2d5)",
              background: active ? "var(--accent, #047857)" : "var(--bg-elev, #fff)",
              color: active ? "#fff" : "var(--text, inherit)",
              cursor: disabled ? "not-allowed" : "pointer",
            }}>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// Slider input for the maturity score (1–5). Native <input type="range">
// styled to sit alongside other form fields, with the live numeric value
// to its right so the user can read the current pick.
function MaturitySlider({ value, onChange }) {
  // The slider always shows a value (defaults to 1 when unset) so the
  // thumb starts at a real position. Empty-string is treated as 1 too.
  const num = value === "" || value == null ? 1 : Number(value);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <input type="range" min="1" max="5" step="1" value={num}
             onChange={e => onChange(Number(e.target.value))}
             style={{ flex: 1 }} />
      <span style={{ fontSize: 13, fontWeight: 600, minWidth: 36, textAlign: "right" }}>{num} / 5</span>
    </div>
  );
}

// ── New gate dialog ─────────────────────────────────────────────────
function NewGateDialog({ defaultPilotId, onClose }) {
  const pilots = D.PILOTS || [];
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    pilotId: defaultPilotId || pilots[0]?.id || "",
    gate: PILOT_GATES[0].key,
    gateDate: "",
    maturityScore: 1,
    valueCaptured: "",
    risksBlockers: "",
    decision: "pending",
    actionBeforeNext: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Auto-fill the date whenever the (pilot, gate) pair changes — but
  // only if the user hasn't already typed a date. Lets the auto-derived
  // value win on first selection without trampling a manual override.
  const [userTouchedDate, setUserTouchedDate] = useState(false);
  useEffect(() => {
    if (userTouchedDate) return;
    const p = pilots.find(x => x.id === form.pilotId);
    const g = PILOT_GATES.find(x => x.key === form.gate);
    if (!p || !p.startDate || !g) return;
    setForm(f => ({ ...f, gateDate: addDaysIso(p.startDate, g.dayOffset) }));
  }, [form.pilotId, form.gate, userTouchedDate]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.pilotId) { setError("Pick a pilot."); return; }
    if (!form.gate)    { setError("Pick a gate."); return; }
    setSaving(true); setError(null);
    try {
      const num = (v) => v === "" || v == null ? null : Number(v);
      await tpAPI.pilotGates.create({
        id: "gate_" + Math.random().toString(36).slice(2, 10),
        pilotId: form.pilotId,
        gate: form.gate,
        gateDate: form.gateDate || today,
        maturityScore: num(form.maturityScore),
        valueCaptured: form.valueCaptured || null,
        risksBlockers: form.risksBlockers || null,
        decision: form.decision || null,
        actionBeforeNext: form.actionBeforeNext || null,
      });
      onClose();
    } catch (e) {
      setError(e.message || "Failed to add gate entry.");
      setSaving(false);
    }
  }

  const pilot = pilots.find(x => x.id === form.pilotId);
  const gateMeta = PILOT_GATES.find(x => x.key === form.gate);
  const derived  = (pilot && pilot.startDate && gateMeta)
    ? addDaysIso(pilot.startDate, gateMeta.dayOffset) : null;

  return (
    <Dialog title="New gate entry" onClose={onClose}
      width="min(720px, 60vw)"
      footer={<>
        <button className="btn btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Add entry"}
        </button>
      </>}>
      <div className="form-row">
        <div className="form-field"><label>Pilot *</label>
          <select value={form.pilotId} onChange={e => set("pilotId", e.target.value)}>
            <option value="">— Select pilot —</option>
            {pilots.map(p => {
              const a = getAccount(p.accountId);
              return <option key={p.id} value={p.id}>{a ? a.name + " · " : ""}{p.name}</option>;
            })}
          </select>
        </div>
        <div className="form-field"><label>Gate *</label>
          <select value={form.gate} onChange={e => set("gate", e.target.value)}>
            {PILOT_GATES.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Date</label>
          <input type="date" value={form.gateDate}
                 onChange={e => {
                   // Clearing the field re-enables auto-derive so the
                   // next pilot/gate pick repopulates it.
                   setUserTouchedDate(!!e.target.value);
                   set("gateDate", e.target.value);
                 }} />
          {derived && (!userTouchedDate || form.gateDate === derived) && (
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Auto-derived from pilot start + {gateMeta.dayOffset} days
            </div>
          )}
          {!pilot?.startDate && (
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Pilot has no start date — pick a date manually.
            </div>
          )}
        </div>
        <div className="form-field" style={{ flex: 2 }}><label>Maturity this gate (1–5)</label>
          <MaturitySlider value={form.maturityScore} onChange={v => set("maturityScore", v)} />
        </div>
      </div>
      {/* New gate entries are always created Pending. Decision is set
          via the toggles in the Edit dialog (or Mark Done from the row). */}
      <div className="form-field"><label>Value captured this gate (quantified where possible)</label>
        <textarea rows="2" value={form.valueCaptured}
                  onChange={e => set("valueCaptured", e.target.value)}
                  placeholder="hours saved, findings closed, maturity movement, customer quote…" />
      </div>
      <div className="form-field"><label>Risks / blockers</label>
        <textarea rows="2" value={form.risksBlockers}
                  onChange={e => set("risksBlockers", e.target.value)}
                  placeholder="what could derail the next gate" />
      </div>
      <div className="form-field"><label>Action before next gate</label>
        <textarea rows="2" value={form.actionBeforeNext}
                  onChange={e => set("actionBeforeNext", e.target.value)}
                  placeholder="single concrete action, with owner where applicable" />
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

// ── Edit gate dialog ────────────────────────────────────────────────
function EditGateDialog({ entry, onClose }) {
  const [form, setForm] = useState({
    gate: entry.gate || PILOT_GATES[0].key,
    gateDate: entry.gateDate || "",
    maturityScore: entry.maturityScore ?? 1,
    valueCaptured: entry.valueCaptured || "",
    risksBlockers: entry.risksBlockers || "",
    decision: entry.decision || "pending",
    actionBeforeNext: entry.actionBeforeNext || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  // `overrideDecision` lets the "Mark Done" footer button force a
  // decision (typically "go") without the user first flipping the
  // toggles. Passing undefined uses whatever the toggles currently say.
  async function handleSave(overrideDecision) {
    setSaving(true); setError(null);
    try {
      const num = (v) => v === "" || v == null ? null : Number(v);
      const decisionToSave = overrideDecision || form.decision || null;
      await tpAPI.pilotGates.update(entry.id, {
        gate: form.gate,
        gateDate: form.gateDate || null,
        maturityScore: num(form.maturityScore),
        valueCaptured: form.valueCaptured || null,
        risksBlockers: form.risksBlockers || null,
        decision: decisionToSave,
        actionBeforeNext: form.actionBeforeNext || null,
      });
      // Auto-create the next gate only when this save advances the pilot
      // with a "go". A "no-go" ends the pilot at this gate, so seeding a
      // further gate would be wrong. Best-effort: a next-gate failure
      // doesn't block closing the dialog — the primary save succeeded.
      const wasPending = !entry.decision || entry.decision === "pending";
      const isNowGo    = decisionToSave === "go";
      if (wasPending && isNowGo) {
        const next = nextPilotGate(form.gate);
        if (next) {
          const pilot   = (D.PILOTS || []).find(p => p.id === entry.pilotId);
          const already = (D.PILOT_GATES || []).some(g => g.pilotId === entry.pilotId && g.gate === next.key);
          if (!already) {
            await createNextGateSafely({
              id: "gate_" + Math.random().toString(36).slice(2, 10),
              pilotId: entry.pilotId,
              gate: next.key,
              gateDate: pilot?.startDate ? addDaysIso(pilot.startDate, next.dayOffset) : null,
              maturityScore: 1,
              decision: "pending",
            });
          }
        }
      }
      onClose();
    } catch (e) {
      setError(e.message || "Failed to save.");
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true); setError(null);
    try {
      await tpAPI.pilotGates.remove(entry.id);
      onClose();
    } catch (e) {
      setError(e.message || "Failed to delete.");
      setSaving(false);
    }
  }

  return (
    <Dialog title="Edit gate entry" onClose={onClose}
      width="min(720px, 60vw)"
      footer={<div style={{ display: "flex", width: "100%", gap: 8 }}>
        {confirmDel ? (
          <>
            <span className="muted" style={{ fontSize: 12, alignSelf: "center", marginRight: "auto" }}>Delete this gate entry?</span>
            <button className="btn btn-sm" onClick={() => setConfirmDel(false)}>Cancel</button>
            <button className="btn btn-sm" style={{ background: "var(--red, #b91c1c)", color: "#fff" }} onClick={handleDelete} disabled={saving}>{saving ? "Deleting…" : "Delete"}</button>
          </>
        ) : (
          <>
            <button className="btn btn-sm" style={{ marginRight: "auto" }} onClick={() => setConfirmDel(true)}>Delete</button>
            <button className="btn btn-sm" onClick={onClose}>Cancel</button>
            {(!entry.decision || entry.decision === "pending") && (
              <button className="btn btn-sm"
                      onClick={() => handleSave(form.decision === "no-go" ? "no-go" : "go")}
                      disabled={saving}
                      title="Save and mark this gate Done. Uses the current Go/No-go toggle; defaults to Go.">
                {saving ? "…" : "Mark Done"}
              </button>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => handleSave()} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
          </>
        )}
      </div>}>
      <div className="form-row">
        <div className="form-field"><label>Gate</label>
          <select value={form.gate} onChange={e => set("gate", e.target.value)}>
            {PILOT_GATES.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Date</label>
          <input type="date" value={form.gateDate} onChange={e => set("gateDate", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field" style={{ flex: 1 }}><label>Maturity this gate (1–5)</label>
          <MaturitySlider value={form.maturityScore} onChange={v => set("maturityScore", v)} />
        </div>
        <div className="form-field" style={{ flex: 1 }}><label>Status</label>
          <GateDecisionToggles decision={form.decision} onChange={v => set("decision", v)} />
        </div>
      </div>
      <div className="form-field"><label>Value captured this gate</label>
        <textarea rows="2" value={form.valueCaptured} onChange={e => set("valueCaptured", e.target.value)} />
      </div>
      <div className="form-field"><label>Risks / blockers</label>
        <textarea rows="2" value={form.risksBlockers} onChange={e => set("risksBlockers", e.target.value)} />
      </div>
      <div className="form-field"><label>Action before next gate</label>
        <textarea rows="2" value={form.actionBeforeNext} onChange={e => set("actionBeforeNext", e.target.value)} />
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

// ── Gatelog tab ─────────────────────────────────────────────────────
function PilotsGatelogTab({ filterPilotId, onClearFilter }) {
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pilotFilter, setPilotFilter] = useState(filterPilotId || "all");
  const [gateFilter, setGateFilter] = useState("all");
  // 4-way decision filter (all | pending | go | no-go). Defaults to
  // "all" so the table shows every gate; rows are reverse-alphabetically
  // sorted by decision (pending → no-go → go) so what still needs a
  // verdict surfaces at the top.
  const [decisionFilter, setDecisionFilter] = useState("all");
  const [markingId, setMarkingId] = useState(null);
  const [error, setError] = useState(null);

  // Quick Mark Done from the row. When the entry has no captured context
  // yet (all three text fields empty), open the edit dialog instead so
  // the user gets a chance to fill in valueCaptured / risksBlockers /
  // actionBeforeNext. None of those fields are mandatory — they can save
  // straight from the dialog if they want. When the entry already has
  // context, marking done is a one-click action that also seeds the next
  // gate via the shared auto-next-gate logic in EditGateDialog's save.
  async function quickMarkDone(entry) {
    const blank = !(entry.valueCaptured || "").trim()
               && !(entry.risksBlockers || "").trim()
               && !(entry.actionBeforeNext || "").trim();
    if (blank) { setEditing(entry); return; }
    setMarkingId(entry.id); setError(null);
    try {
      await tpAPI.pilotGates.update(entry.id, { decision: "go" });
      const next = nextPilotGate(entry.gate);
      if (next) {
        const pilot   = (D.PILOTS || []).find(p => p.id === entry.pilotId);
        const already = (D.PILOT_GATES || []).some(g => g.pilotId === entry.pilotId && g.gate === next.key);
        if (!already) {
          await createNextGateSafely({
            id: "gate_" + Math.random().toString(36).slice(2, 10),
            pilotId: entry.pilotId,
            gate: next.key,
            gateDate: pilot?.startDate ? addDaysIso(pilot.startDate, next.dayOffset) : null,
            maturityScore: 1,
            decision: "pending",
          });
        }
      }
    } catch (e) {
      setError(e.message || "Failed to mark done.");
    } finally {
      setMarkingId(null);
    }
  }

  // Sync local pilot filter with the parent-supplied filter (from List tab
  // cross-linking). If it changes after mount, follow it.
  useEffect(() => {
    if (filterPilotId) setPilotFilter(filterPilotId);
  }, [filterPilotId]);

  const all = D.PILOT_GATES || [];
  // Primary sort: decision string reverse-alphabetical → "pending"
  // (starts with 'p') beats "no-go" beats "go". JS sort is stable, so
  // within the same decision the BE's gateDate ASC ordering is preserved.
  const entries = all.filter(e => {
    if (pilotFilter !== "all" && e.pilotId !== pilotFilter) return false;
    if (gateFilter !== "all" && e.gate !== gateFilter) return false;
    if (decisionFilter !== "all") {
      const dec = e.decision || "pending";
      if (dec !== decisionFilter) return false;
    }
    return true;
  }).sort((a, b) => {
    const da = a.decision || "pending";
    const db = b.decision || "pending";
    return da === db ? 0 : (da < db ? 1 : -1);
  });

  const filteredPilot = pilotFilter !== "all"
    ? (D.PILOTS || []).find(p => p.id === pilotFilter) : null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8 }}>
        {filteredPilot ? (
          <div className="muted" style={{ fontSize: 12 }}>
            Filtered to <strong>{filteredPilot.name}</strong>
            {" "}
            <button className="btn btn-sm" onClick={() => { setPilotFilter("all"); onClearFilter && onClearFilter(); }} style={{ marginLeft: 8 }}>Clear</button>
          </div>
        ) : <div />}
        <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ New gate entry</button>
      </div>
      <div className="card">
        <FilterToolbar count={entries.length} total={all.length}>
          <select value={pilotFilter} onChange={e => { setPilotFilter(e.target.value); onClearFilter && onClearFilter(); }}>
            <option value="all">All pilots</option>
            {(D.PILOTS || []).map(p => {
              const a = getAccount(p.accountId);
              return <option key={p.id} value={p.id}>{a ? a.name + " · " : ""}{p.name}</option>;
            })}
          </select>
          <select value={gateFilter} onChange={e => setGateFilter(e.target.value)}>
            <option value="all">All gates</option>
            {PILOT_GATES.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
          <select value={decisionFilter} onChange={e => setDecisionFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="no-go">No-go</option>
            <option value="go">Go</option>
          </select>
        </FilterToolbar>
        {error && <div className="form-error" style={{ margin: "8px 16px" }}>{error}</div>}
        {entries.length === 0 ? (
          <div className="card-body muted">
            No gate entries match. Click <strong>+ New gate entry</strong> to add one.
          </div>
        ) : (
          <table className="tbl tbl-flush">
            <thead><tr>
              <th>Client</th>
              <th>Project</th>
              <th>Gate</th>
              <th>Date</th>
              <th className="right">Maturity</th>
              <th>Value captured</th>
              <th>Risks / blockers</th>
              <th>Go / No-go</th>
              <th>Action before next gate</th>
              <th></th>
            </tr></thead>
            <tbody>
              {entries.map(e => {
                const p = (D.PILOTS || []).find(x => x.id === e.pilotId);
                const a = p ? getAccount(p.accountId) : null;
                const overdue = isGateOverdue(e);
                // Overdue rows lean on row-level color: every cell text
                // turns red so the row reads "this slipped" at a glance.
                // Decision badge keeps its own tone so the user can still
                // see at-a-glance what's still pending.
                const rowStyle = overdue
                  ? { cursor: "default", color: "var(--red, #b91c1c)" }
                  : { cursor: "default" };
                return (
                  <tr key={e.id} style={rowStyle}>
                    <td>{a?.name || <span className="muted">—</span>}</td>
                    <td>{p?.name || <span className="muted">—</span>}</td>
                    <td>{pilotGateLabel(e.gate)}</td>
                    <td>{fmtDateShort(e.gateDate)}{overdue && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600 }}>OVERDUE</span>}</td>
                    <td className="right numeric">{e.maturityScore ?? <span className="muted">—</span>}</td>
                    <td style={{ maxWidth: 240, whiteSpace: "pre-wrap" }}>{e.valueCaptured || <span className="muted">—</span>}</td>
                    <td style={{ maxWidth: 200, whiteSpace: "pre-wrap" }}>{e.risksBlockers || <span className="muted">—</span>}</td>
                    <td><Badge tone={pilotGateDecisionTone(e.decision)}>{pilotGateDecisionLabel(e.decision)}</Badge></td>
                    <td style={{ maxWidth: 240, whiteSpace: "pre-wrap" }}>{e.actionBeforeNext || <span className="muted">—</span>}</td>
                    <td className="right" onClick={ev => ev.stopPropagation()}>
                      <div style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                        {(!e.decision || e.decision === "pending") && (
                          <button className="btn btn-sm"
                                  title="Mark this gate done (Go). Empty rows open the editor first so you can add context."
                                  disabled={markingId === e.id}
                                  onClick={() => quickMarkDone(e)}>
                            {markingId === e.id ? "…" : "Mark Done"}
                          </button>
                        )}
                        <button className="btn btn-sm btn-icon" title="Edit" onClick={() => setEditing(e)}>✎</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {showNew && <NewGateDialog defaultPilotId={pilotFilter !== "all" ? pilotFilter : null} onClose={() => setShowNew(false)} />}
      {editing && <EditGateDialog entry={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

window.PilotsGatelogTab = PilotsGatelogTab;
